import {Typography, TypographyOwnProps} from '@mui/material';
import React, {useState} from 'react';
import AurSvgIcon from '../aur-svg-icon/AurSvgIcon';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import AurPopup from '../aur-popup/AurPopup';
import {t} from 'i18next';
import AurTooltip from '../aur-tooltip/AurTooltip';
import AurButton from '../aur-button/AurButton';
import {AurLightTooltip} from '../aur-light-tooltip/aur-light-tooltip';
import {ONLY_DATE_SHOW_FORMAT} from '../../../constants/constants';

export interface AurAlertProps {
  alertTitle?: string;
  triggeredOn?: string;
  alertDescription?: string;
  alertType?: string;
  showArrowCTA?: boolean;
  showDatasets?: boolean;
  datasets?: string[];
  projectName?: string;
  workspaceName?: string;
  deleteButtonHandler?: () => void;
  prevButtonHandler?: () => void;
  onPressAlertHandler?: () => void;
  onPressNavigationHandler?: () => void;
  budgetDataLength?: number;
  titleAlignment?: TypographyOwnProps['align'];
}

export type AurAlertDataType = AurAlertProps & {
  userAlertId?: number;
};

const AurAlert: React.FC<AurAlertProps> = (props: AurAlertProps) => {
  const {
    alertTitle,
    projectName,
    workspaceName,
    showDatasets = false,
    showArrowCTA = false,
    datasets,
    triggeredOn,
    alertDescription,
    deleteButtonHandler,
    prevButtonHandler,
    onPressAlertHandler,
    onPressNavigationHandler,
    alertType,
    titleAlignment,
    budgetDataLength,
  } = props;
  const tooltipClasses = {
    arrow: 'aur-report-dataset_tooltip-arrow',
    tooltip: 'aur-report-dataset_tooltip-tooltip',
  };

  const [showDeleteConfirmationAlert, setShowDeleteConfirmationAlert] =
    useState(false);
  const onOkClick = () => {
    setShowDeleteConfirmationAlert(false);
    deleteButtonHandler && deleteButtonHandler();
  };
  const datasetsToDisplay =
    datasets && datasets.length > 3 ? datasets.slice(0, 3) : datasets;
  const datasetsMoreItemsToDisplay = datasets && datasets.length - 3;
  const formattedDate = getUTCDateTimeForDisplay(
    triggeredOn,
    ONLY_DATE_SHOW_FORMAT
  );

  return (
    <>
      <button
        className="aur-alert-container"
        data-testid="aur-alert-container"
        aria-label={t('aria.alertCard', {alertTitle})}
        onClick={onPressAlertHandler}
      >
        <div className="aur-alert-icon-container">
          {alertType === 'BudgetTracker' ? (
            <div className="dollar-icon">
              <AurSvgIcon src="../assests/images/dollar_icon.svg" ariaHidden />
            </div>
          ) : (
            <AurSvgIcon src="../assests/images/report-warning.svg" ariaHidden />
          )}
        </div>
        <div className="aur-alert-text-container">
          <div className="aur-alert-title-main-container">
            <div className="aur-alert-title-sub-container">
              <div className="aur-alert-title-text-container">
                <Typography
                  role="alert"
                  aria-label={`${t('common.alert.alert')}${alertTitle}`}
                  tabIndex={0}
                  className={`typo-text subhead4_size alert-text-color`}
                  align={titleAlignment}
                >
                  {alertTitle}
                </Typography>
                <AurLightTooltip title={t('common.yearMonthDate')}>
                  <Typography
                    className={`typo-text label2_size alert-date-stamp-color`}
                  >
                    {formattedDate}
                  </Typography>
                </AurLightTooltip>
              </div>
              <div className="aur-alert-title-icon-container">
                {alertType === 'BudgetTracker' ? null : (
                  <button
                    className="aur-alert-title-icon-inner-container hidden"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirmationAlert(true);
                    }}
                    data-testid="aur-alert-trash-icon-button"
                    tabIndex={0}
                    aria-label={`${t('common.alert.cliclToDeleteAlert')} ${alertTitle} ${'common.alert.afterConfirmation'}`}
                  >
                    <AurSvgIcon
                      src="../assests/images/report-delete.svg"
                      ariaHidden
                    />
                  </button>
                )}

                {showDatasets && (
                  <AurTooltip
                    title={
                      <React.Fragment>
                        <Typography className="typo-text body4_size aur-dataset-tooltip-text aur-dataset-title">
                          Datasets involved
                        </Typography>
                        {datasetsToDisplay?.map(
                          (dataset: string, index: number) => (
                            <Typography
                              className="typo-text label2_size aur-dataset-tooltip-text"
                              key={`date-set-${index}-${dataset}`}
                            >
                              {dataset}
                            </Typography>
                          )
                        )}
                        {datasets && datasets.length > 3 ? (
                          <Typography className="typo-text label2_size aur-dataset-tooltip-text">{`(+${datasetsMoreItemsToDisplay} more)`}</Typography>
                        ) : (
                          ''
                        )}
                      </React.Fragment>
                    }
                    className="aur-alert-tooltip"
                    PointerPlacement="left"
                    disableInteractive
                    classes={tooltipClasses}
                    tabIndex={0}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {name: 'offset', options: {offset: [0, -6]}},
                        ],
                      },
                    }}
                  >
                    <div
                      className="aur-alert-title-icon-inner-container"
                      data-testid="report-dataset-button"
                    >
                      <AurSvgIcon
                        src="../assests/images/report-dataset.svg"
                        ariaHidden
                      />
                    </div>
                  </AurTooltip>
                )}
                {showArrowCTA && (
                  <button
                    className="aur-alert-title-icon-inner-container"
                    onClick={(e) => {
                      e.stopPropagation();
                      prevButtonHandler && prevButtonHandler();
                    }}
                    data-testid="aur-alert-right-arrow-button"
                    aria-label={`${t('common.alert.clickToOpen')} ${alertTitle} ${t('common.alert.inSpecificPage')}`}
                  >
                    <AurSvgIcon
                      src="../assests/images/report-arrow-forward.svg"
                      ariaHidden
                    />
                  </button>
                )}
              </div>
            </div>
            <div className="aur-alert-description-conatiner">
              <Typography
                className={`typo-text body3_size alert-description-color`}
                aria-label={alertDescription}
              >
                {alertDescription}
              </Typography>
            </div>
          </div>

          {alertType === 'BudgetTracker' ? (
            <AurButton
              classNames="budgetTAlertBtn"
              endIcon={<AurSvgIcon src="images/right_button_arrow.svg" />}
              type="continue"
              size="Medium"
              label={`${t('common.viewLabel')} ${budgetDataLength} ${t('common.projectCapitalLabel')}${budgetDataLength! > 1 ? t('common.s') : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onPressNavigationHandler && onPressNavigationHandler();
              }}
              ariaLabel={`${t('common.viewLabel')} ${budgetDataLength} ${t('common.projectLabel')}${budgetDataLength! > 1 ? t('common.s') : ''}`}
              ariaDescribedBy="budget-projects-hint"
              title={`${t('common.viewDetailsForLabel')} ${budgetDataLength} ${t('common.projectLabel')}${budgetDataLength! > 1 ? t('common.s') : ''}`}
              tabIndex={0}
              dataTestId="view-budget-project"
            />
          ) : (
            <div className="aur-alert-name-container">
              {projectName && (
                <Typography
                  className={`typo-text label2_size alert-name-color alert-name-truncate`}
                >
                  {`${t('common.projectCapitalLabel')}: ${projectName}`}
                </Typography>
              )}
              {workspaceName && (
                <Typography
                  className={`typo-text label2_size alert-name-color alert-name-truncate`}
                >
                  {` ${t('common.workspace')}: ${workspaceName}`}
                </Typography>
              )}
            </div>
          )}
        </div>
      </button>
      {showDeleteConfirmationAlert && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={showDeleteConfirmationAlert}
          onOk={onOkClick}
          onCancel={() => setShowDeleteConfirmationAlert(false)}
          popupHeading={t('common.deleteAlertPopup.heading')}
          popupTitle={t('common.deleteAlertPopup.popupTitle')}
          aria-description={t('common.deleteAlertPopup.description')}
          popupContent={t('common.deleteAlertPopup.description')}
          labelActionCancel={t('common.deleteAlertPopup.buttonCancel')}
          labelActionOk={t('common.deleteAlertPopup.buttonYes')}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
        />
      )}
    </>
  );
};

export default AurAlert;
